import { useEffect, useRef, useState } from 'react';
import { Upload } from 'antd';
import { useApp } from 'contexts/app';
import ImageService from 'services/image';
import { getPreviewSource } from '../MultiImagePicker/useComponent';

const useComponent = ({ value, onChange, acceptedFormats, maxFileSize }) => {
    const { notification } = useApp();
    const [loading, setLoading] = useState(false);
    const [pasting, setPasting] = useState(false);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [libraryOpen, setLibraryOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [localPreviewSrc, setLocalPreviewSrc] = useState('');
    const uploadRef = useRef(null);
    const objectUrlRef = useRef('');
    const mountedRef = useRef(true);

    const acceptValue = (acceptedFormats || []).join(',');
    const serverPreviewSrc = value ? getPreviewSource(value) : '';
    const previewSrc = localPreviewSrc || serverPreviewSrc;
    const hasValue = !!previewSrc;

    const resetObjectUrl = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = '';
        }
    };

    const setOptimisticPreview = (file) => {
        resetObjectUrl();
        const objectUrl = URL.createObjectURL(file);
        objectUrlRef.current = objectUrl;
        setLocalPreviewSrc(objectUrl);
    };

    const clearOptimisticPreview = () => {
        resetObjectUrl();
        setLocalPreviewSrc('');
    };

    useEffect(() => {
        // Chỉ dọn preview tạm khi đã có URL ảnh chính thức từ server,
        // để ảnh vừa chọn luôn hiển thị xuyên suốt lúc upload.
        if (!serverPreviewSrc) {
            return;
        }

        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = '';
        }
        setLocalPreviewSrc('');
    }, [serverPreviewSrc]);

    useEffect(() => () => {
        mountedRef.current = false;
        resetObjectUrl();
    }, []);

    const validateImageFile = (file) => {
        const validType = (acceptedFormats || []).some((f) => {
            if (f.endsWith('/*')) return file.type.startsWith(f.replace('/*', '/'));
            if (f.startsWith('.')) return file.name.toLowerCase().endsWith(f);
            return file.type === f;
        });

        if (!validType) {
            notification.error('Định dạng file không được hỗ trợ');
            return false;
        }

        if (file.size > maxFileSize) {
            notification.error('File quá lớn');
            return false;
        }

        return true;
    };

    const uploadFile = async (file) => {
        if (mountedRef.current) {
            setLoading(true);
        }
        try {
            const svc = new ImageService();
            const fd = new FormData();
            fd.append('file', file);
            const result = await svc.upload(fd);
            if (!mountedRef.current) {
                return;
            }
            const uploadedPreview = getPreviewSource(result);
            if (uploadedPreview) {
                clearOptimisticPreview();
                setLocalPreviewSrc(uploadedPreview);
            }
            onChange(result);
        } catch {
            if (!mountedRef.current) {
                return;
            }
            clearOptimisticPreview();
            notification.error('Tải ảnh thất bại. Vui lòng thử lại');
        } finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    };

    const handleUpload = async (file) => {
        if (!validateImageFile(file)) {
            return Upload.LIST_IGNORE;
        }

        setOptimisticPreview(file);
        await uploadFile(file);

        return Upload.LIST_IGNORE;
    };

    const pasteImageFromClipboard = async () => {
        setSheetOpen(false);

        if (!navigator?.clipboard?.read) {
            notification.error('Trình duyệt chưa hỗ trợ đọc ảnh từ clipboard');
            return;
        }

        if (mountedRef.current) {
            setPasting(true);
        }
        try {
            const items = await navigator.clipboard.read();
            let imageBlob = null;

            for (const item of items) {
                const imageType = item.types.find((type) => String(type || '').startsWith('image/'));
                if (!imageType) {
                    continue;
                }

                imageBlob = await item.getType(imageType);
                if (imageBlob) {
                    break;
                }
            }

            if (!imageBlob) {
                notification.error('Không tìm thấy hình ảnh trong clipboard');
                return;
            }

            const ext = (String(imageBlob.type || '').split('/')[1] || 'png').toLowerCase();
            const file = new File([imageBlob], `pasted-image-${Date.now()}.${ext}`, { type: imageBlob.type || 'image/png' });

            if (!validateImageFile(file)) {
                return;
            }

            setOptimisticPreview(file);
            await uploadFile(file);
        } catch {
            if (mountedRef.current) {
                clearOptimisticPreview();
                notification.error('Không thể dán hình từ clipboard');
            }
        } finally {
            if (mountedRef.current) {
                setPasting(false);
            }
        }
    };

    const triggerUpload = () => {
        setSheetOpen(false);
        setTimeout(() => uploadRef.current?.querySelector('input[type="file"]')?.click(), 300);
    };

    const handlePreview = () => {
        setSheetOpen(false);
        setPreviewImage(previewSrc);
        setPreviewOpen(true);
    };

    const handleRemove = () => {
        setSheetOpen(false);
        setConfirmDelete(false);
        clearOptimisticPreview();
        onChange(null);
    };

    const handleLibrarySelect = (images) => {
        clearOptimisticPreview();
        if (images?.length >= 1) onChange(images[0]);
        setLibraryOpen(false);
    };

    const openLibrary = () => {
        setSheetOpen(false);
        setTimeout(() => setLibraryOpen(true), 300);
    };

    return {
        loading,
        pasting,
        sheetOpen,
        setSheetOpen,
        previewOpen,
        setPreviewOpen,
        previewImage,
        setPreviewImage,
        libraryOpen,
        setLibraryOpen,
        confirmDelete,
        setConfirmDelete,
        uploadRef,
        acceptValue,
        previewSrc,
        hasValue,
        handleUpload,
        pasteImageFromClipboard,
        triggerUpload,
        handlePreview,
        handleRemove,
        handleLibrarySelect,
        openLibrary,
    };
};

export default useComponent;
